/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { RoughCashboxesListTableData } from '../../../components/Service/Common/AddCashboxesModal/AddCashboxesModalConfig';
import {
    cutEmptyParamsIncludingArrays,
    DATA_UNAVAILABLE,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import apiClient from '../apiClient';
import {
    CreateCashBoxTaskRequestParams,
    CreateCashBoxTaskResponseDto,
    CashBoxTasksListDto,
    CashBoxTasksListRequestParams,
    RoughCashboxesListDto,
    RoughCashboxesListRequestParams,
    SuitableTargetVersionsDto,
    SuitableTargetVersionsParams,
    DeviceServiceListItem,
    ServiceOperationBody,
    UploadInitializationFileBody,
} from './serviceClient.typings';
import { fusApiClient } from '../fusApiClient';

const FACTORY_NUMBERS_SEARCH_LIMIT = 20;

const ROUGH_CASHBOXES_LIST_SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof RoughCashboxesListTableData]?: string } = {
    taxNumber: 'cashBoxTaxNumbers',
    cashRegisterFactoryNumber: 'cashRegisterFactoryNumbers',
    deviceFactoryNumber: 'deviceFactoryNumbers',
    storeTaxNumber: 'storeTaxNumbers',
    companyTaxNumber: 'companyTaxNumbers',
};

const getSearchParamName = (searchType: keyof RoughCashboxesListTableData): string => {
    let searchParam = ROUGH_CASHBOXES_LIST_SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

const adjustRoughCashboxesListFilterParams = (params: RoughCashboxesListRequestParams) => {
    const { search_type, search_text, ...newParams } = params;

    return cutEmptyParamsIncludingArrays({
        [getSearchParamName(search_type as keyof RoughCashboxesListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
        ...newParams,
    });
};

async function getRoughCashboxesList(params: RoughCashboxesListRequestParams): Promise<RoughCashboxesListDto> {
    return apiClient.post<RoughCashboxesListDto>(
        '1.0.0/service/cashboxes',
        adjustRoughCashboxesListFilterParams(params),
    );
}

async function updateServiceCashboxesList(cashBoxTaxNumbers: string[]): Promise<RoughCashboxesListDto> {
    return apiClient.post<RoughCashboxesListDto>(
        '1.0.0/service/cashboxes',
        cutEmptyParamsIncludingArrays({ cashBoxTaxNumbers, limit: cashBoxTaxNumbers.length, afterKeyTaxNumber: null }),
    );
}

async function getCashboxTaskHistory(params: CashBoxTasksListRequestParams): Promise<CashBoxTasksListDto> {
    return apiClient.get<CashBoxTasksListDto>('1.0.0/service/tasks/history', params);
}

async function getCashboxesTasksStatuses(params: CashBoxTasksListRequestParams): Promise<CashBoxTasksListDto> {
    return apiClient.get<CashBoxTasksListDto>('1.0.0/service/tasks/status', params);
}

async function createCashboxTask(params: CreateCashBoxTaskRequestParams): Promise<CreateCashBoxTaskResponseDto> {
    return apiClient.post<CreateCashBoxTaskResponseDto>('1.0.0/service/tasks', params);
}

async function createCashboxesTasks(params: CreateCashBoxTaskRequestParams[]): Promise<CreateCashBoxTaskResponseDto[]> {
    return apiClient.post<CreateCashBoxTaskResponseDto[]>('1.0.0/service/tasks/multi', params);
}

function downloadCashBoxLogsFile(logId: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/service/logs/${logId}`);
}

async function getSuitableTargetVersions(params: SuitableTargetVersionsParams): Promise<SuitableTargetVersionsDto> {
    if (process.env.REACT_APP_MOCK_API)
        return apiClient.get<SuitableTargetVersionsDto>(`v2/firmwares/${params.devicePlatform}`, {
            versionCurrent: params.currentVersion,
        });
    else
        return fusApiClient.get<SuitableTargetVersionsDto>(`v2/firmwares/${params.devicePlatform}`, {
            versionCurrent: params.currentVersion,
        });
}

function uploadInitialisationFile(payload: UploadInitializationFileBody) {
    const { file, operation } = payload;

    const formData = new FormData();

    formData.append('file', file);
    formData.append('operation', operation);
    return apiClient.post<{}>('1.0.0/service/uploadInitFile', formData);
}

function searchFactoryNumbers(factoryNumber: string) {
    return apiClient.get<string[]>('1.0.0/service/factoryNumbers', {
        factoryNumber,
        limit: FACTORY_NUMBERS_SEARCH_LIMIT,
    });
}

function devicesList(factoryNumbers: string[]) {
    return apiClient.post<DeviceServiceListItem[]>('1.0.0/service/devices', {
        factoryNumbers,
    });
}

function performDeviceOperations(payload: ServiceOperationBody) {
    return apiClient.post<string>('1.0.0/service/deviceOperation', payload);
}

export const serviceClient = {
    getRoughCashboxesList,
    updateServiceCashboxesList,
    getCashboxTaskHistory,
    getCashboxesTasksStatuses,
    createCashboxTask,
    createCashboxesTasks,
    downloadCashBoxLogsFile,
    getSuitableTargetVersions,
    uploadInitialisationFile,
    searchFactoryNumbers,
    devicesList,
    performDeviceOperations,
};
